"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createMeshDocumentUtils = void 0;
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const dbNodes = __importStar(require("../dbNodes/dbNodes"));
const typeguards = __importStar(require("../typeguards"));
const operationConvertors_1 = require("../uiNodes/operationConvertors");
const tasks_1 = require("../uiNodes/tasks");
const isRawNode_1 = require("./isRawNode");
const meshGrouping_1 = require("./meshGrouping");
// Builds mesh documents (artifacts, inputs, name) from connected graphs. Does
// not check access: callers pass only meshes the caller may see.
const createMeshDocumentUtils = ({ basicGraph, prefix, params: { id: artifactDocumentId, implementationId, borrowerOrganizationId, artifacts, dependencies, cfSlugToNameMap, }, }) => {
    // For a given artifactId,
    // this function will return all artifactId that are between
    // given artifactId and a sanitizedNode.
    // this is useful to monitor the progress of the sanitization for a given raw ode
    const _getSanitizedArtifactIds = (artifactId) => {
        return basicGraph.bfsTraversal({
            startNodes: basicGraph.getOutputs(artifactId),
            search: 'outputs',
            criteria: (currentArtifactId) => {
                // check is current artifact is post tapeBuilderArtifact.
                const currentArtifact = basicGraph.getArtifactById(currentArtifactId);
                if (typeguards.isTapeBuilderTask(currentArtifact.configuration)) {
                    if ((0, tasks_1.isPostSanitizeOperation)(currentArtifact.configuration.operation)) {
                        // this operation is pos-sanitize, this is the node
                        // we want to wait for, and stop our search.
                        return {
                            matches: true,
                            continueBranch: false,
                        };
                    }
                    // this is a pre-sanitize operation (like pre-filter)
                    // we want to include this node to wait for, but
                    // not stop our search here.
                    return {
                        matches: true,
                        continueBranch: true,
                    };
                }
                // this is not a tape-builder node.
                // this is BBPL node like master loan tape
                // we do not want to include this and we do
                // not want to continue search in this path.
                return {
                    matches: false,
                    continueBranch: false,
                };
            },
        });
    };
    // what an uploaded file must contain: one entry per post-sanitize node fed
    // by the raw node (from `_getSanitizedArtifactIds`) that describes its input.
    const _getDataDictionary = (sanitizedArtifactIds) => {
        return sanitizedArtifactIds
            .map(basicGraph.getArtifactById)
            .flatMap(({ artifactSlug, configuration }) => {
            if (!typeguards.isTapeBuilderTask(configuration) ||
                !(0, tasks_1.isPostSanitizeOperation)(configuration.operation)) {
                return [];
            }
            const entry = (0, operationConvertors_1.getOperationDataDictionaryEntry)(configuration, {
                artifactSlug,
            });
            return entry ? [entry] : [];
        })
            .sort((a, b) => a.artifactSlug.localeCompare(b.artifactSlug));
    };
    // returns upload config for the artifact.
    const getUploadConfig = ({ artifactId, }) => {
        const artifact = basicGraph.getArtifactById(artifactId);
        if ((0, isRawNode_1.isRawNode)(artifact)) {
            const uploadConfig = artifact.configuration.uploadConfig;
            if (!uploadConfig) {
                // this artifact is not configured for upload.
                return null;
            }
            return uploadConfig;
        }
        return null;
    };
    // returns DataPipeline.MeshInput corresponding to the artifactId or null
    const _getMeshInput = (artifactId) => {
        const artifact = basicGraph.getArtifactById(artifactId);
        if (!(0, isRawNode_1.isRawNode)(artifact)) {
            // this is not a raw node, or one w/o upload config r
            return null;
        }
        if (!dbNodes.rawCashBalanceFeed.hasArtifactType(artifact)) {
            // for simple file nodes, just get the data from uploadConfig.
            const uploadConfig = artifact.configuration.uploadConfig;
            if (!uploadConfig) {
                // this artifact is not configured for upload.
                return null;
            }
            const extension = uploadConfig.fileExtension;
            (0, common_1.finleyAssert)(extension, `extension was missing from uploadConfig for ${artifactId}`);
            const sanitizedArtifactIds = _getSanitizedArtifactIds(artifactId);
            return {
                artifactId,
                type: 'file',
                extension,
                baseFilename: uploadConfig.baseFilename,
                friendlyName: uploadConfig.externalDisplayName,
                loanProductSlug: artifact.loanProductSlug,
                creditFacilitySlug: artifact.creditFacilitySlug,
                sanitizedArtifactIds,
                dataDictionary: _getDataDictionary(sanitizedArtifactIds),
            };
        }
        const uploadConfig = artifact.configuration.uploadConfig;
        if (!uploadConfig) {
            // this artifact is not configured for upload.
            return null;
        }
        // cash balance feed are special.
        // they may specify the input as tape or form or fetch
        const cashUploadOptions = uploadConfig.cashUploadOptions ?? [];
        if (cashUploadOptions.includes('tape')) {
            const extension = uploadConfig.fileExtension;
            (0, common_1.finleyAssert)(extension, `extension was missing from uploadConfig for ${artifactId}`);
            const sanitizedArtifactIds = _getSanitizedArtifactIds(artifactId);
            return {
                artifactId,
                type: 'file',
                extension: 'csv',
                baseFilename: uploadConfig.baseFilename,
                loanProductSlug: artifact.loanProductSlug,
                creditFacilitySlug: artifact.creditFacilitySlug,
                friendlyName: uploadConfig.externalDisplayName,
                sanitizedArtifactIds,
                dataDictionary: _getDataDictionary(sanitizedArtifactIds),
            };
        }
        if (cashUploadOptions.includes('form')) {
            // find the artifactId for the cashBalanceFeedContext
            // this will be the artifact that is generated from the form.
            const cashBalanceFeedContexts = basicGraph
                .getRecursiveOutputs(artifactId)
                .map(basicGraph.getArtifactById)
                .filter(dbNodes.cashBalancesFeedContext.hasArtifactType)
                .filter(({ creditFacilitySlug }) => creditFacilitySlug === artifact.creditFacilitySlug);
            (0, common_1.finleyAssert)(cashBalanceFeedContexts.length === 1, `invalid cashBalanceFeedContext.length [${cashBalanceFeedContexts.length}] corresponding to rawCashBalanceFeed: ${artifactId}`);
            const creditFacilitySlug = cashBalanceFeedContexts[0].creditFacilitySlug;
            return {
                artifactId: cashBalanceFeedContexts[0].id,
                type: 'cashBalance',
                creditFacilitySlug,
                friendlyName: uploadConfig.externalDisplayName,
                creditFacilityId: basicGraph.cfSlugToId(creditFacilitySlug),
                sanitizedArtifactIds: [],
            };
        }
        return null;
    };
    const _getMeshInputs = (artifactIds) => {
        return artifactIds
            .map(_getMeshInput)
            .filter(common_1.typeguards.isNotNullOrUndefined);
    };
    const toDataPipelineArtifact = (artifact) => {
        const { id, type, artifactSlug, configuration } = artifact;
        const uploadConfig = getUploadConfig({ artifactId: id });
        return {
            id,
            type,
            artifactSlug,
            configuration,
            externalDisplayName: uploadConfig?.externalDisplayName || artifactSlug,
        };
    };
    const getMeshGroupingMetadata = (artifactId) => {
        const uploadConfig = getUploadConfig({ artifactId });
        return {
            hasUploadConfig: !!uploadConfig,
            explicitMeshName: uploadConfig?.meshName ?? null,
        };
    };
    // return data pipeline meshes from given graphs.
    const getDataPipelineMeshes = (graphs) => {
        return (0, meshGrouping_1.groupDataPipelineMeshes)({
            connectedGraphs: graphs,
            getArtifactMetadata: getMeshGroupingMetadata,
        }).meshes;
    };
    // returns unique loanProducts and creditFacilities found in the nodes.
    const _getLpCfSlugsForArtifactIds = (artifactIds) => {
        const lpSlugs = new Set();
        const cfSlugs = new Set();
        artifactIds.forEach((artifactId) => {
            const artifact = basicGraph.getArtifactById(artifactId);
            if (artifact.loanProductSlug) {
                lpSlugs.add(artifact.loanProductSlug);
            }
            if (artifact.creditFacilitySlug) {
                cfSlugs.add(artifact.creditFacilitySlug);
            }
        });
        return { lpSlugs, cfSlugs };
    };
    // this function only converts.
    // caller must ensure the access to the artifactIds.
    const toMeshDocument = (artifactIds) => {
        (0, common_1.finleyAssert)(artifactIds.length > 0, `${prefix}: artifactIds is empty`);
        // generate a stable id for the mesh document.
        // by combining the artifactDocumentId and 1st artifact id.
        const sortedArtifactIds = artifactIds.sort();
        const meshDocumentId = finley_types_1.DataPipeline.generateMeshDocumentId({
            artifactDocumentId,
            meshArtifactId: sortedArtifactIds[0],
        });
        const meshArtifacts = artifactIds
            .map(basicGraph.getArtifactById)
            .map(toDataPipelineArtifact);
        const meshDependencies = dependencies
            .filter(({ inputArtifactId, outputArtifactId }) => artifactIds.includes(inputArtifactId) &&
            artifactIds.includes(outputArtifactId))
            .map(({ inputArtifactId, outputArtifactId }) => ({
            source: inputArtifactId,
            target: outputArtifactId,
        }));
        const { lpSlugs, cfSlugs } = _getLpCfSlugsForArtifactIds(artifactIds);
        const meshInputs = _getMeshInputs(artifactIds);
        const fundingRequests = artifacts
            .filter(({ id }) => artifactIds.includes(id))
            .filter(dbNodes.fundingRequest.hasArtifactType)
            .map((artifact) => {
            const { creditFacilitySlug } = artifact;
            // find any userInputNeeded (inputType=verificationApproval) for the funding requests.
            // this will be set only on verification flows.
            const verificationApproval = artifacts.find((verificationApprovalArtifact) => {
                return (dbNodes.userInputNeeded.hasArtifactType(verificationApprovalArtifact) &&
                    verificationApprovalArtifact.configuration.inputType ===
                        'verificationApproval' &&
                    verificationApprovalArtifact.creditFacilitySlug ===
                        creditFacilitySlug);
            });
            return {
                artifactId: artifact.id,
                creditFacilitySlug,
                verificationArtifactId: verificationApproval?.id ?? null,
                creditFacilityId: basicGraph.cfSlugToId(creditFacilitySlug),
            };
        });
        const assignments = artifacts
            .filter(({ id }) => artifactIds.includes(id))
            .filter((artifact) => {
            return (dbNodes.userInputNeeded.hasArtifactType(artifact) &&
                artifact.configuration.inputType === 'assignement');
        })
            .map(({ id }) => ({ artifactId: id }));
        // mesh name is used as "title" or header for the mesh.
        const meshName = (0, meshGrouping_1.makeMeshName)({
            explicitMeshName: (0, meshGrouping_1.getFirstExplicitMeshName)({
                artifactIds,
                getArtifactMetadata: getMeshGroupingMetadata,
            }),
            cfSlugs: Array.from(cfSlugs),
            cfSlugToNameMap,
        });
        return {
            meshDocumentId,
            implementationId,
            artifacts: meshArtifacts,
            dependencies: meshDependencies,
            cfSlugs: Array.from(cfSlugs),
            lpSlugs: Array.from(lpSlugs),
            meshName,
            meshInputs,
            // for some nodes we render
            // special links in the upload
            fundingRequests,
            assignments,
            // mesh document need to know the borrower org.
            // as on lender dashboard they will be looking at multiple
            // borrowers.
            borrowerOrganizationId,
        };
    };
    return { getUploadConfig, getDataPipelineMeshes, toMeshDocument };
};
exports.createMeshDocumentUtils = createMeshDocumentUtils;
//# sourceMappingURL=meshDocuments.js.map